import React, { useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { faSave } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";

import { Create as CreateTemplateModal } from "~/noteTemplate/modal";
import { setStreamTemplate } from "~/stream/StreamSettings";
import Menu from "~/ui/menu";
import { useModals } from "~/ui/modals";
import toast from "~/ui/toast";

import type { SaveAsTemplate_note$key } from "~/__generated__/SaveAsTemplate_note.graphql";
import type { SaveAsTemplate_viewer$key } from "~/__generated__/SaveAsTemplate_viewer.graphql";

const viewerFragment = graphql`
	fragment SaveAsTemplate_viewer on Viewer {
		canCreateWorkspaceTemplate

		account {
			billingAccount {
				hasAccessToPromptHeadings: hasAccessToFeature(feature: prompt_headings)
			}
		}
	}
`;

const noteFragment = graphql`
	fragment SaveAsTemplate_note on Note {
		id
		title
		stream {
			id
		}
		rawDraftJson
		schemaVersion
	}
`;

export default function SaveAsTemplate({
	viewer: viewerKey,
	note: noteKey,
}: {
	viewer: SaveAsTemplate_viewer$key;
	note: SaveAsTemplate_note$key;
}) {
	const modals = useModals();
	const viewer = useFragment(viewerFragment, viewerKey);
	const note = useFragment(noteFragment, noteKey);

	const onSaveTemplateClick = useCallback(async () => {
		const result = await modals.open<{ createdTemplateId: string; setAsDefault: boolean } | null>(({ close }) => (
			<CreateTemplateModal
				close={close}
				initialTitle={note.title}
				// TODO: Is rawDraftJSON often out of date? Otherwise why did we need this editorRef json?
				// initialDoc={this.editorRef.current?.editorView?.state.doc.toJSON() ?? note.rawDraftJson.doc}
				initialDoc={note.rawDraftJson.doc}
				canEditVisibility={viewer.canCreateWorkspaceTemplate}
				offerToSetAsDefault
				hasAccessToPromptHeadings={viewer.account.billingAccount.hasAccessToPromptHeadings}
				schemaVersion={note.schemaVersion}
			/>
		));

		if (result?.setAsDefault) {
			let success;
			try {
				const { noteStreamSetTemplateSetting } = await setStreamTemplate(note.stream.id, result.createdTemplateId);
				success = Boolean(noteStreamSetTemplateSetting);
			} catch (e) {
				success = false;
			}
			if (success) {
				toast.success("Updated series default template.", { title: "Success" });
			} else {
				toast.error("Failed to set series default template.", { title: "Error" });
			}
		} else if (result) {
			toast.success("Saved template successfully.", { title: "Success" });
		}
	}, [
		modals,
		note.title,
		note.rawDraftJson.doc,
		note.schemaVersion,
		note.stream.id,
		viewer.canCreateWorkspaceTemplate,
		viewer.account.billingAccount.hasAccessToPromptHeadings,
	]);

	return (
		<Menu.Item icon={faSave} onSelect={onSaveTemplateClick} data-test-id="note-header-dropdown-savetemplate-item">
			Save as note template…
		</Menu.Item>
	);
}
